import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Award,
  Ban,
  Check,
  Copy,
  Download,
  FileSignature,
  History,
  Loader,
  Mail,
  RefreshCw,
  Search,
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import { confirmDialog, promptDialog } from '../../lib/confirmStore';
import { notifyToast } from '../../lib/toastStore';
import Modal from '../shared/Modal';
import SigningTemplateManager from './SigningTemplateManager';

/**
 * Admin E-Signatures console: every native sign request in one place
 * (status filters, search, copy link, reminder, void, downloads, audit
 * trail) plus the workspace signing-template manager with tags.
 */

interface SignRequestListItem {
  id: string;
  signRequestId: string;
  applicationId: string | null;
  templateId: string | null;
  templateName: string;
  documentName: string;
  signerName: string;
  signerEmail: string;
  status: string;
  sentBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  tokenExpiresAt: string | null;
  signingUrl: string | null;
  completedAt: string | null;
  hasSignedPdf: boolean;
  hasCertificate: boolean;
  auditTrail: Array<Record<string, any>>;
  signingMode: string | null;
  signers: Array<{
    signerId: string;
    kind: string;
    label: string;
    name: string;
    email: string;
    order: number;
    status: string;
    invitedAt: string | null;
    signedAt: string | null;
    signingUrl: string;
  }> | null;
}

interface EsignaturesPageProps {
  /** Open the linked applicant (AdminDashboard preview pattern), when wired. */
  onViewApplication?: (applicationId: string) => void;
}

const STATUS_FILTERS = ['all', 'sent', 'viewed', 'partially_signed', 'completed', 'expired', 'voided'] as const;

const STATUS_STYLES: Record<string, string> = {
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  viewed: 'bg-amber-50 text-amber-700 border-amber-200',
  partially_signed: 'bg-blue-50 text-blue-700 border-blue-200',
  completing: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expired: 'bg-rose-50 text-rose-700 border-rose-200',
  voided: 'bg-slate-100 text-slate-600 border-slate-300',
};

function statusLabel(status: string): string {
  if (status === 'partially_signed') return 'Partially Signed';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatWhenTime(value: any): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const PAGE_SIZE = 100;

export default function EsignaturesPage({ onViewApplication }: EsignaturesPageProps) {
  const [tab, setTab] = useState<'requests' | 'templates'>('requests');
  const [items, setItems] = useState<SignRequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // ADM-18: cursor paging state. `total` is the server-reported
  // collection total (null when the server could not provide one —
  // counts are then labelled as loaded-only, never invented).
  const [total, setTotal] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [auditItem, setAuditItem] = useState<SignRequestListItem | null>(null);

  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const token = await auth.currentUser?.getIdToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const readPagingHeaders = (res: Response) => {
    const rawTotal = res.headers.get('X-Total-Count');
    const parsed = rawTotal != null ? parseInt(rawTotal, 10) : NaN;
    setTotal(Number.isFinite(parsed) ? parsed : null);
    setNextCursor(res.headers.get('X-Next-Cursor'));
  };

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/esign/requests?limit=${PAGE_SIZE}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load sign requests');
      readPagingHeaders(res);
      setItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load sign requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `/api/admin/esign/requests?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(nextCursor)}`,
        { headers }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load sign requests');
      readPagingHeaders(res);
      if (Array.isArray(data)) {
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          return [...prev, ...data.filter((i: SignRequestListItem) => !seen.has(i.id))];
        });
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to load more sign requests');
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextCursor, loadingMore]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: items.length };
    for (const item of items) {
      map[item.status] = (map[item.status] || 0) + 1;
    }
    return map;
  }, [items]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [item.signerName, item.signerEmail, item.templateName, item.documentName, item.signRequestId, item.id]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, statusFilter, search]);

  const handleCopyLink = async (item: SignRequestListItem) => {
    if (!item.signingUrl) return;
    try {
      await navigator.clipboard.writeText(item.signingUrl);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId((c) => (c === item.id ? null : c)), 1600);
    } catch {
      await promptDialog('Copy the signing link:', item.signingUrl);
    }
  };

  const handleReminder = async (item: SignRequestListItem) => {
    if (!(await confirmDialog(`Send a reminder email for "${item.documentName || item.templateName || 'this document'}" to ${item.signerName} (${item.signerEmail})? The email includes their signing link.`, { confirmLabel: 'Send reminder' }))) return;
    setBusyId(item.id);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/esign/requests/${item.id}/reminder`, { method: 'POST', headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send reminder');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to send reminder');
    } finally {
      setBusyId(null);
    }
  };

  const handleVoid = async (item: SignRequestListItem) => {
    if (!(await confirmDialog(`Void signing request ${item.signRequestId}? The signer's link will stop working immediately.`))) return;
    setBusyId(item.id);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`/api/admin/esign/requests/${item.id}/void`, { method: 'POST', headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to void request');
      await loadRequests();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to void request');
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (item: SignRequestListItem, kind: 'signed' | 'certificate') => {
    setBusyId(item.id);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/esign/requests/${item.id}/download?kind=${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.signRequestId}-${kind}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      notifyToast('error', err.message || 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  const isTerminal = (status: string) => status === 'completed' || status === 'voided' || status === 'expired';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-brand" />
            E-Signatures
          </h1>
          <p className="text-caption text-slate-500 mt-0.5">
            Track every signing request, resend or void links, download signed documents, and manage reusable signing templates.
          </p>
        </div>
        {tab === 'requests' && (
          <button
            type="button"
            onClick={loadRequests}
            disabled={loading}
            className="px-3 py-2 bg-white border border-slate-200 text-caption font-bold rounded-xl text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Tab menu */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {(
          [
            { id: 'requests', label: 'Sign Requests' },
            { id: 'templates', label: 'Sign Templates' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-caption font-bold border-b-2 -mb-px cursor-pointer transition ${
              tab === t.id ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'templates' ? (
        <SigningTemplateManager embedded onClose={() => {}} />
      ) : (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-caption font-bold border cursor-pointer transition ${
                    statusFilter === s
                      ? 'bg-brand text-white border-brand'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-brand/40'
                  }`}
                >
                  {s === 'all' ? 'All' : statusLabel(s)}
                  <span className={`ml-1.5 ${statusFilter === s ? 'text-white/80' : 'text-slate-400'}`}>
                    {loading && items.length === 0 ? '–' : counts[s] || 0}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative ml-auto w-full sm:w-72">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search signer, email, template, SIG id…"
                className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-brand/20 focus:outline-none"
              />
            </div>
          </div>

          {/* List */}
          {loading && items.length === 0 ? (
            // L1: an honest loading state — labelled, with skeleton rows
            // in the table's shape — instead of a bare spinner beside
            // status counts reading 0 (which are wrong data, not
            // placeholders, while the list is in flight).
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" role="status" aria-live="polite">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                <Loader className="h-4 w-4 animate-spin text-brand" />
                <p className="text-caption font-bold text-slate-600">Loading sign requests…</p>
              </div>
              <div className="divide-y divide-slate-100" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="px-4 py-3.5 flex items-center gap-4 animate-pulse">
                    <div className="h-3.5 bg-slate-100 rounded w-28 shrink-0" />
                    <div className="h-3.5 bg-slate-100 rounded w-44 shrink-0" />
                    <div className="h-3.5 bg-slate-100 rounded w-20 shrink-0" />
                    <div className="h-3.5 bg-slate-100 rounded w-16 ml-auto shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          ) : loadError && items.length === 0 ? (
            <div className="bg-white border border-rose-200 rounded-2xl p-6 text-center space-y-2">
              <AlertCircle className="h-8 w-8 text-rose-500 mx-auto" />
              <p className="text-sm font-bold text-slate-800">Could not load sign requests</p>
              <p className="text-caption text-slate-500">{loadError}</p>
              <button
                type="button"
                onClick={loadRequests}
                className="px-4 py-2 btn-primary text-white text-caption font-bold rounded-xl cursor-pointer"
              >
                Try again
              </button>
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="text-center py-14 bg-white border border-dashed border-slate-200 rounded-2xl">
              <FileSignature className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-bold text-slate-600">No sign requests match</p>
              <p className="text-caption text-slate-400 mt-1">
                {items.length === 0
                  ? 'Requests sent from an applicant\u2019s Sign tab will appear here.'
                  : 'Try a different status filter or search term.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[880px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3">Request</th>
                      <th className="px-4 py-3">Signer</th>
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Sent</th>
                      <th className="px-4 py-3">Expires</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                        <td className="px-4 py-3">
                          <p className="font-mono text-[11px] font-bold text-slate-700">{item.signRequestId}</p>
                          <p className="text-caption text-slate-500 truncate max-w-[220px]">
                            {item.documentName || item.templateName || 'Document'}
                          </p>
                          {item.templateName && item.documentName && item.templateName !== item.documentName && (
                            <p className="text-[11px] text-slate-500 truncate max-w-[220px]">Template: {item.templateName}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-bold text-slate-800">{item.signerName || '—'}</p>
                          <p className="text-caption text-slate-500 truncate max-w-[200px]">{item.signerEmail || '—'}</p>
                          {Array.isArray(item.signers) && item.signers.length > 1 && (
                            <div className="mt-1 space-y-0.5">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                {item.signers.filter((s) => s.status === 'signed').length} of {item.signers.length} signed ·{' '}
                                {item.signingMode === 'strict_order' ? 'Strict order' : 'Any order'}
                              </p>
                              {[...item.signers]
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                .map((s) => (
                                  <p key={s.signerId} className="text-[11px] text-slate-500 whitespace-nowrap">
                                    {(s.order ?? 0) + 1}. {s.name} —{' '}
                                    <span
                                      className={
                                        s.status === 'signed'
                                          ? 'text-emerald-600 font-bold'
                                          : s.status === 'invited'
                                          ? 'text-amber-600 font-bold'
                                          : 'text-slate-400 font-semibold'
                                      }
                                    >
                                      {s.status === 'signed'
                                        ? `Signed${s.signedAt ? ` ${formatWhen(s.signedAt)}` : ''}`
                                        : s.status === 'invited'
                                        ? 'Awaiting signature'
                                        : 'Waiting for earlier signer'}
                                    </span>
                                  </p>
                                ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.applicationId ? (
                            onViewApplication ? (
                              <button
                                type="button"
                                onClick={() => onViewApplication(item.applicationId!)}
                                className="font-mono text-[11px] font-bold text-brand hover:underline cursor-pointer"
                                title="Open applicant"
                              >
                                {item.applicationId}
                              </button>
                            ) : (
                              <span className="font-mono text-[11px] text-slate-600">{item.applicationId}</span>
                            )
                          ) : (
                            <span className="text-caption text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full border ${
                              STATUS_STYLES[item.status] || 'bg-slate-50 text-slate-600 border-slate-200'
                            }`}
                          >
                            {statusLabel(item.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-caption text-slate-600 whitespace-nowrap">{formatWhen(item.createdAt)}</td>
                        <td className="px-4 py-3 text-caption text-slate-600 whitespace-nowrap">{formatWhen(item.tokenExpiresAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleCopyLink(item)}
                              disabled={!item.signingUrl}
                              className="p-1.5 text-slate-400 hover:text-brand bg-slate-50 hover:bg-brand/10 rounded-lg cursor-pointer transition disabled:opacity-40"
                              title="Copy signing link"
                            >
                              {copiedId === item.id ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReminder(item)}
                              disabled={busyId === item.id || isTerminal(item.status)}
                              className="p-1.5 text-slate-400 hover:text-brand bg-slate-50 hover:bg-brand/10 rounded-lg cursor-pointer transition disabled:opacity-40"
                              title={isTerminal(item.status) ? 'Reminders only apply to open requests' : 'Send reminder email'}
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                            {item.status === 'completed' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDownload(item, 'signed')}
                                  disabled={busyId === item.id || !item.hasSignedPdf}
                                  className="p-1.5 text-slate-400 hover:text-emerald-600 bg-slate-50 hover:bg-emerald-50 rounded-lg cursor-pointer transition disabled:opacity-40"
                                  title="Download signed PDF"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDownload(item, 'certificate')}
                                  disabled={busyId === item.id || !item.hasCertificate}
                                  className="p-1.5 text-slate-400 hover:text-emerald-600 bg-slate-50 hover:bg-emerald-50 rounded-lg cursor-pointer transition disabled:opacity-40"
                                  title="Download audit certificate"
                                >
                                  <Award className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {!isTerminal(item.status) && (
                              <button
                                type="button"
                                onClick={() => handleVoid(item)}
                                disabled={busyId === item.id}
                                className="p-1.5 text-slate-400 hover:text-rose-600 bg-slate-50 hover:bg-rose-50 rounded-lg cursor-pointer transition disabled:opacity-40"
                                title="Void request"
                              >
                                <Ban className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setAuditItem(item)}
                              className="p-1.5 text-slate-400 hover:text-brand bg-slate-50 hover:bg-brand/10 rounded-lg cursor-pointer transition"
                              title="View audit trail"
                            >
                              <History className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ADM-18: loaded/total disclosure + paging control. The
              status counts above cover the loaded rows; this line says
              how much of the collection that is. */}
          {items.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-caption text-slate-500">
                {total != null
                  ? nextCursor
                    ? `Showing ${items.length} of ${total} sign requests. Status counts cover the loaded requests.`
                    : `Showing all ${total} sign requests.`
                  : nextCursor
                    ? `Showing ${items.length} loaded sign requests. Status counts cover the loaded requests.`
                    : `Showing ${items.length} sign requests.`}
              </p>
              {nextCursor && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="ml-auto px-4 py-2 bg-white border border-slate-200 text-caption font-bold rounded-xl text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {loadingMore && <Loader className="h-3.5 w-3.5 animate-spin" />}
                  {loadingMore
                    ? 'Loading…'
                    : total != null
                      ? `Load more (${total - items.length} remaining)`
                      : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Audit trail modal */}
      {auditItem && (
        <Modal onClose={() => setAuditItem(null)} ariaLabel="Sign Request Audit Trail">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Audit Trail</h3>
                <p className="text-caption text-slate-500 font-mono mt-0.5">{auditItem.signRequestId}</p>
              </div>
              <button
                type="button"
                onClick={() => setAuditItem(null)}
                className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Close
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              {Array.isArray(auditItem.auditTrail) && auditItem.auditTrail.length > 0 ? (
                <div className="space-y-3">
                  {auditItem.auditTrail.map((ev: any, i: number) => (
                    <div key={i} className="flex gap-3 text-xs border-b border-slate-50 pb-3 last:border-0 last:pb-0">
                      <div className="w-2 h-2 rounded-full bg-brand mt-1 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-bold text-slate-800">{ev.event || 'Event'}</p>
                        <p className="text-slate-400">{formatWhenTime(ev.timestamp)}</p>
                        {ev.details && <p className="text-slate-500 mt-0.5">{ev.details}</p>}
                        {ev.ipAddress && <p className="text-slate-400 font-mono text-[10px] mt-0.5">IP: {ev.ipAddress}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-caption text-slate-400 text-center py-6">No audit events recorded for this request.</p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
